import type { FC } from 'react';
import { useCallback, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, useTheme } from '@mui/material';

import { noDataAvailableStyles } from 'src/theme/sharedStyles';
import type { DashboardWidgetProps } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import WidgetFrame from 'src/components/dashboard/DashboardPage/components/WidgetFrame';
import SortableHorizontalStackedBarChart from 'src/components/followUp/common/BaseWidgets/SortableHorizontalStackedBarChart/SortableHorizontalStackedBarChart';
import type { StackedNumbersList } from 'src/components/followUp/common/types';
import NoResultsToShowAdjustFiltersWarning from 'src/components/followUp/common/warning/NoResultsToShowAdjustFiltersWarning';
import WidgetFooterLegend from 'src/components/followUp/common/WidgetFooterLegend/WidgetFooterLegend';
import WidgetHeaderWithPill from 'src/components/followUp/common/WidgetHeaderWithPill/WidgetHeaderWithPill';
import useGenerateWidgetEndpoint from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

import { useExportOptions } from './hooks';

const ReasonsForLeavingWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const ariaId = useId();
  const { t } = useTranslation('followUp');
  const endpoint = useGenerateWidgetEndpoint(
    apiPaths.WIDGET_DATA.REASONS_FOR_LEAVING,
    config.id,
  );
  const { colors } = useTheme();

  const { data, isFetching, errorMsg, refetch, queryFilters, comparisonData } =
    useWidgetQuery<StackedNumbersList>(endpoint);

  const labels = useMemo(
    () => [
      {
        name: t('statistics.reasonsForLeavingWidget.labels.rankedFirst'),
        color: colors.secondary[800],
        borderColor: colors.secondary[900],
        comparisonColor: comparisonData && colors.comparison[800],
      },
      {
        name: t('statistics.reasonsForLeavingWidget.labels.rankedSecond'),
        color: colors.secondary[700],
        borderColor: colors.secondary[800],
        comparisonColor: comparisonData && colors.comparison[600],
      },
      {
        name: t('statistics.reasonsForLeavingWidget.labels.rankedThird'),
        color: colors.secondary[600],
        borderColor: colors.secondary[700],
        comparisonColor: comparisonData && colors.comparison[400],
      },
      {
        color: 'transparent',
        borderColor: 'transparent',
        name: t('common:noDataAvailable'),
        customBackgroundSx: noDataAvailableStyles.noDataAvailable,
      },
    ],
    [colors, comparisonData],
  );

  const transformStacks = useCallback(
    (stacks: Array<{ stackLabel: string; value: number }>) =>
      stacks.map((stack) => ({
        ...stack,
        stackLabel: t(
          `statistics.reasonsForLeavingWidget.labels.${stack.stackLabel}`,
        ),
      })),
    [t],
  );

  const mapBars = useCallback(
    (widgetData?: StackedNumbersList) =>
      widgetData?.results.map((bar) => {
        const correspondingComparisonData = comparisonData?.results.find(
          (comparisonBar) => comparisonBar.label == bar.label,
        )?.stacks;

        return {
          label: bar.label,
          stacks: transformStacks(bar.stacks),
          comparison: correspondingComparisonData
            ? {
                stacks: transformStacks(correspondingComparisonData),
              }
            : undefined,
        };
      }) ?? [],
    [t, comparisonData, transformStacks],
  );

  const bars = useMemo(() => mapBars(data), [mapBars, data, comparisonData]);
  const comparisonBars = useMemo(
    () => (comparisonData ? mapBars(comparisonData) : undefined),
    [mapBars, comparisonData],
  );

  const exportOptions = useExportOptions({
    widgetName: config.name,
    bars,
    comparisonBars,
    stackLabels: labels,
    queryFilters,
  });

  return (
    <WidgetFrame
      ariaId={ariaId}
      loading={isFetching}
      error={errorMsg}
      refetch={refetch}
      exportOptions={exportOptions}
      header={<WidgetHeaderWithPill title={config.name} ariaId={ariaId} />}
      footer={
        data?.totalObjects === 0 ? (
          <Stack
            sx={{
              height: '100%',
              justifyContent: 'center',
              alignItems: 'flex-start',
              px: 3,
            }}
          >
            <NoResultsToShowAdjustFiltersWarning />
          </Stack>
        ) : (
          <WidgetFooterLegend labels={labels} />
        )
      }
    >
      <SortableHorizontalStackedBarChart stackLabels={labels} bars={bars} />
    </WidgetFrame>
  );
};
export default ReasonsForLeavingWidget;
